import { botReason, createQualification, syntheticCohort } from "./analytics/bots";
import { BEACON_PATH, CLIENT_EVENTS, ID_PATTERN, MAX_BATCH_EVENTS, MAX_BODY_BYTES, SESSION_COOKIE, VISITOR_COOKIE, safeProperties, safeReferrer, utmQuerystring, type BeaconPayload, type BrowserEvent, type PageContext, type Properties } from "./analytics/contract";

let initialized = false;
let disabled = false;
let page: PageContext | undefined;
let visitorId = "";
let sessionId = "";
let previousUrl = "";
let pageStartedAt = 0;
let queue: BrowserEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let dwellTimer: ReturnType<typeof setTimeout> | undefined;
let qualification: ReturnType<typeof createQualification> | undefined;
let engagementCheckpoint = 0;

function readCookie(name: string): string | undefined {
  try { return document.cookie.split("; ").find((item) => item.startsWith(`${name}=`))?.slice(name.length + 1); } catch { return undefined; }
}
function writeCookie(name: string, value: string, maxAge: number) {
  try { document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`; } catch { /* Storage is optional. */ }
}
function identity(name: string, maxAge: number): string {
  const existing = readCookie(name);
  const value = existing && ID_PATTERN.test(existing) ? existing : crypto.randomUUID();
  writeCookie(name, value, maxAge);
  return value;
}
function attending() { return document.visibilityState === "visible" && document.hasFocus(); }
function snapshot(): PageContext {
  let timezone = "";
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* Optional diagnostic. */ }
  return {
    path: location.pathname.slice(0, 2048), pageviewId: crypto.randomUUID(), title: document.title.slice(0, 256),
    referrer: safeReferrer(previousUrl || document.referrer), querystring: utmQuerystring(location.search),
    language: navigator.language.slice(0, 64), timezone: timezone.slice(0, 128),
    screenWidth: screen.width, screenHeight: screen.height, viewportWidth: innerWidth, viewportHeight: innerHeight,
    webdriver: navigator.webdriver === true,
  };
}
function qualify() {
  if (!page || !qualification) return;
  page.qualification = qualification.read(performance.now());
  page.focusedMs = Math.round(qualification.focusedTime());
}
function flush() {
  clearTimeout(flushTimer);
  flushTimer = undefined;
  if (!initialized || disabled || !queue.length) return;
  qualify();
  const admitted = queue.filter((event) => !syntheticCohort(navigator.userAgent, event.context, location.hostname) || event.context.qualification);
  if (!admitted.length) return;
  queue = queue.filter((event) => !admitted.includes(event));
  // Refresh the 30-minute browser session only while recording activity.
  sessionId = identity(SESSION_COOKIE, 1800);
  while (admitted.length) {
    const events = admitted.splice(0, MAX_BATCH_EVENTS);
    const payload: BeaconPayload = { visitorId, sessionId, sentAt: Date.now(), events };
    let body = JSON.stringify(payload);
    while (new TextEncoder().encode(body).byteLength > MAX_BODY_BYTES && events.length > 1) {
      admitted.unshift(events.pop()!);
      body = JSON.stringify(payload);
    }
    if (new TextEncoder().encode(body).byteLength > MAX_BODY_BYTES) continue;
    let sent = false;
    try { sent = navigator.sendBeacon?.(BEACON_PATH, new Blob([body], { type: "application/json" })) ?? false; } catch { /* Fall back to fetch. */ }
    if (!sent) void fetch(BEACON_PATH, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true, credentials: "same-origin" }).catch(() => {});
  }
}
function scheduleFlush() {
  if (!flushTimer) flushTimer = setTimeout(flush, 1000);
}
function armDwell() {
  clearTimeout(dwellTimer);
  if (!qualification || !attending()) return;
  if (qualification.read(performance.now())) { flush(); return; }
  dwellTimer = setTimeout(() => { qualify(); recordEngagement(); flush(); }, qualification.remaining() + 1);
}
function recordEngagement() {
  if (!page || !qualification) return;
  qualify();
  const focusedMs = Math.round(qualification.focusedTime());
  const delta = focusedMs - engagementCheckpoint;
  if (delta > 0) {
    engagementCheckpoint = focusedMs;
    enqueue("page_engagement", { engagement_time_msec: delta }, page);
  }
}
function enqueue(event: BrowserEvent["event"], properties: Properties = {}, eventContext?: PageContext) {
  if (disabled || typeof window === "undefined" || navigator.webdriver) return;
  const context = eventContext ?? (page?.path === location.pathname ? page : snapshot());
  // Bound memory while a suspected visitor remains unqualified.
  if (queue.length >= 50) return;
  queue.push({ event, properties: safeProperties(properties), timestamp: Date.now(), context });
  scheduleFlush();
}

/** Existing product events keep their names and discovery attribution. */
export function captureEvent(event: string, properties: Properties = {}) {
  if (!(CLIENT_EVENTS as readonly string[]).includes(event)) return;
  enqueue(event as BrowserEvent["event"], properties);
}

export function trackPageView() {
  if (disabled || !initialized || page?.path === location.pathname) return;
  if (page) {
    recordEngagement();
    enqueue("$pageleave", { duration_ms: Math.round(performance.now() - pageStartedAt) }, page);
    flush();
    // An unqualified previous page must never leak through with a later
    // page's qualification. Retained events keep their own context objects.
    queue = queue.filter((event) => event.context.path === location.pathname);
    previousUrl = `${location.origin}${page.path}`;
  }
  page = snapshot();
  pageStartedAt = performance.now();
  engagementCheckpoint = 0;
  qualification = createQualification(pageStartedAt, attending());
  // Components can report product events before the deferred pageview island.
  // Attach only this page's buffered events to its qualification/context.
  for (const event of queue) if (event.context.path === page.path) event.context = page;
  queue.unshift({ event: "$pageview", timestamp: Math.min(Date.now(), ...queue.filter((event) => event.context === page).map((event) => event.timestamp)), context: page, properties: {} });
  scheduleFlush();
  armDwell();
}

export function initializeAnalytics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  disabled = Boolean(botReason(navigator.userAgent, { webdriver: navigator.webdriver === true }));
  if (disabled) { queue = []; return; }
  visitorId = identity(VISITOR_COOKIE, 365 * 86400);
  sessionId = identity(SESSION_COOKIE, 1800);
  const attendance = () => {
    qualification?.attendance(performance.now(), attending());
    recordEngagement();
    if (!attending()) flush();
    armDwell();
  };
  const activity = (event: Event) => {
    qualification?.activity(performance.now(), event.type, event.isTrusted);
    qualify();
    if (page?.qualification) flush();
  };
  for (const type of ["pointerdown", "keydown", "touchstart", "wheel"]) window.addEventListener(type, activity, { capture: true, passive: true });
  document.addEventListener("visibilitychange", attendance);
  window.addEventListener("focus", attendance);
  window.addEventListener("blur", attendance);
  window.addEventListener("pagehide", () => {
    qualification?.attendance(performance.now(), false);
    recordEngagement();
    enqueue("$pageleave", { duration_ms: Math.round(performance.now() - pageStartedAt) });
    flush();
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) { queue = []; page = undefined; previousUrl = ""; trackPageView(); }
  });
  trackPageView();
}
